import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { accountingAPI } from '@/lib/api';
import { toast } from 'sonner';

const DELIVERY_METHODS = [
  { v: 'mail',           label: 'Mail' },
  { v: 'pickup',         label: 'Pickup' },
  { v: 'hand_delivered', label: 'Hand Delivered' },
  { v: 'other',          label: 'Other' },
];

/**
 * Inline payment method editor for a single Batch Line.
 * Auto-inherits Preferred Method; KR can override.
 * Emits `onUpdated(newLine)` after successful save.
 */
export default function PaymentMethodEditor({ batchId, line, onUpdated, disabled }) {
  const initialMethod = (line.actual_payment_method || line.payment_method || 'ach').toLowerCase();
  const [method, setMethod] = useState(initialMethod);
  const [ach, setAch] = useState(line.ach_payload || { payment_date: '', ach_reference: '', processor: '', notes: '' });
  const [check, setCheck] = useState(line.check_payload || { check_number: '', check_date: '', delivery_method: 'mail', notes: '' });
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    if (savedFlash) {
      const t = setTimeout(() => setSavedFlash(false), 1400);
      return () => clearTimeout(t);
    }
  }, [savedFlash]);

  const isConfirmed = !!line.method_confirmed_at;
  const isZeroNet = line.line_type === 'zero_net';

  const save = async () => {
    setSaving(true);
    try {
      const payload = { method };
      if (method === 'ach') payload.ach = ach;
      if (method === 'check') payload.check = check;
      const { data } = await accountingAPI.setBatchLineMethod(batchId, line.batch_line_id, payload);
      onUpdated?.(data);
      setSavedFlash(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save payment method');
    } finally { setSaving(false); }
  };

  if (isZeroNet) {
    return (
      <div className="text-xs text-slate-500 italic px-3 py-2 bg-slate-50 rounded border border-slate-200">
        Zero-net line — no payment payload required. Statements will be marked <b>fully_applied</b>.
      </div>
    );
  }

  return (
    <div className="p-3 bg-slate-50 rounded-md border border-slate-200 space-y-3" data-testid={`method-editor-${line.batch_line_id}`}>
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium uppercase text-slate-600">Payment Method</Label>
        {isConfirmed && (
          <div className="flex items-center gap-1 text-xs text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" /> Confirmed
          </div>
        )}
      </div>

      <Select value={method} onValueChange={setMethod} disabled={disabled || saving}>
        <SelectTrigger className="h-9" data-testid={`method-select-${line.batch_line_id}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ach">ACH (Direct Deposit)</SelectItem>
          <SelectItem value="check">Check</SelectItem>
        </SelectContent>
      </Select>

      {method === 'ach' && (
        <div className="grid grid-cols-2 gap-2">
          <Field label="Payment Date">
            <Input type="date" value={ach.payment_date || ''} onChange={(e) => setAch({ ...ach, payment_date: e.target.value })} disabled={disabled} data-testid={`ach-payment-date-${line.batch_line_id}`} />
          </Field>
          <Field label="ACH Reference #">
            <Input value={ach.ach_reference || ''} onChange={(e) => setAch({ ...ach, ach_reference: e.target.value })} placeholder="ACH-2026-…" disabled={disabled} data-testid={`ach-ref-${line.batch_line_id}`} />
          </Field>
          <Field label="Processor (optional)">
            <Input value={ach.processor || ''} onChange={(e) => setAch({ ...ach, processor: e.target.value })} placeholder="e.g. Chase, ACH Direct" disabled={disabled} />
          </Field>
          <Field label="Notes (optional)">
            <Input value={ach.notes || ''} onChange={(e) => setAch({ ...ach, notes: e.target.value })} disabled={disabled} />
          </Field>
        </div>
      )}

      {method === 'check' && (
        <div className="grid grid-cols-2 gap-2">
          <Field label="Check Number">
            <Input value={check.check_number || ''} onChange={(e) => setCheck({ ...check, check_number: e.target.value })} placeholder="1001" disabled={disabled} data-testid={`check-num-${line.batch_line_id}`} />
          </Field>
          <Field label="Check Date">
            <Input type="date" value={check.check_date || ''} onChange={(e) => setCheck({ ...check, check_date: e.target.value })} disabled={disabled} data-testid={`check-date-${line.batch_line_id}`} />
          </Field>
          <Field label="Delivery Method">
            <Select value={check.delivery_method || 'mail'} onValueChange={(v) => setCheck({ ...check, delivery_method: v })} disabled={disabled}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DELIVERY_METHODS.map((d) => (<SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Notes (optional)">
            <Input value={check.notes || ''} onChange={(e) => setCheck({ ...check, notes: e.target.value })} disabled={disabled} />
          </Field>
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        {savedFlash && <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Saved</span>}
        <Button size="sm" onClick={save} disabled={saving || disabled} data-testid={`save-method-${line.batch_line_id}`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
          Save Method
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wide text-slate-500">{label}</label>
      {children}
    </div>
  );
}
